import React, {
    memo,
    useCallback,
    useState,
    useMemo,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Popover, Icon, Modal, Textarea, Text, Carousel } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './carouselCellStyles';

const useStyles = createUseStyles(styles, 'Carousel');

const CarouselCell = ({
    data,
    otherFields,
    value,
    node,
    isReadOnly,
    inputParams,
    imageSize = 'medium',
    isSquared = false,
    withEmptyPlaceholder,
}) => {
    const [isEllipsisActive, setEllipsisActive] = useState(false);
    const descriptionEl = useRef();

    const [description, setDescription] = useState(data?.[otherFields?.secondaryTextKey]);
    const [isOpen, setIsOpen] = useState();
    const { t } = useWeb5Context();
    const classes = useStyles();

    const carouselCellClassName = classnames('w5-carousel-cell', {
        'w5-carousel-cell--small': imageSize === 'small',
        'w5-carousel-cell--medium': imageSize === 'medium',
        'w5-carousel-cell--big': imageSize === 'big',
    });
    const boxImageClassName = classnames('w5-carousel-cell__img-box', {
        'w5-carousel-cell__img-box--small': imageSize === 'small',
        'w5-carousel-cell__img-box--medium': imageSize === 'medium',
        'w5-carousel-cell__img-box--big': imageSize === 'big',
        'w5-carousel-cell__img-box--squared': isSquared,
        'w5-carousel-cell__img-box__with-popover':
            otherFields?.carouselFieldId && data?.[otherFields.carouselFieldId],
    });
    const carouselPopoverClassName = classnames(
        'w5-carousel-cell__img-box__popover',
        classes.popover,
        { [classes.singleImagePopover]: data?.[otherFields?.carouselFieldId]?.length === 1 },
    );

    useEffect(() => {
        if (isOpen) setDescription(data?.[otherFields?.secondaryTextKey]);
    }, [data, isOpen, otherFields?.secondaryTextKey]);

    useLayoutEffect(() => {
        const el = descriptionEl.current;
        if (!el || !data) return;
        setEllipsisActive(el.offsetWidth < el.scrollWidth);
    }, [data, otherFields?.secondaryTextKey, setEllipsisActive]);

    const [mainImageSrc, setMainImageSrc] = useState(() => {
        return data?.[otherFields?.rowImageSrc] || data?.[otherFields?.rowImagePlaceholder];
    });

    const onLoadMainImageError = useCallback(() => {
        setMainImageSrc(data?.[otherFields?.rowImagePlaceholder]);
    }, [data, otherFields]);

    const onChangeDescription = useCallback(
        (value) => {
            if (!inputParams) return;
            const { onChange } = inputParams;
            const id = data.id || data.Id || data.ID || '';
            onChange({ rowIndex: node.rowIndex, id, field: otherFields?.secondaryTextKey, value });
        },
        [data.ID, data.Id, data.id, inputParams, node.rowIndex, otherFields?.secondaryTextKey],
    );

    const imagesCarousel = useMemo(() => {
        if (!data?.[otherFields?.carouselFieldId]) return null;

        const images = data[otherFields.carouselFieldId].map((image, index) => {
            const imageUrl =
                image[otherFields.carouselImageSrc] || image[otherFields.carouselImagePlaceholder];
            return {
                original: imageUrl,
                thumbnail: imageUrl,
            };
        });

        return <Carousel images={images} />;
    }, [data, otherFields]);

    const imageBox = useMemo(() => {
        const imageBox = (
            <div className={boxImageClassName}>
                <img src={mainImageSrc} alt={value} onError={onLoadMainImageError} />
            </div>
        );

        if (imagesCarousel) {
            return (
                <Popover
                    className={carouselPopoverClassName}
                    placement="right"
                    content={imagesCarousel}
                >
                    {imageBox}
                </Popover>
            );
        }
        return imageBox;
    }, [
        boxImageClassName,
        mainImageSrc,
        value,
        onLoadMainImageError,
        imagesCarousel,
        carouselPopoverClassName,
    ]);

    const showDescriptionForm = useCallback(() => setIsOpen(true), []);

    const onConfirm = useCallback(() => {
        onChangeDescription(description);
        setIsOpen(false);
    }, [description, onChangeDescription]);

    const onClose = useCallback(() => setIsOpen(false), []);

    const modalEditProps = useMemo(
        () => ({
            isOpen,
            size: 'small',
            onConfirm,
            onCancel: onClose,
            onRequestClose: onClose,
            cancelText: t('action_cancel'),
            confirmText: t('action_save'),
            overlayClassName: classes.overlay,
        }),
        [classes.overlay, isOpen, onClose, onConfirm, t],
    );

    const modalViewProps = useMemo(
        () => ({
            isOpen,
            size: 'medium',
            onCancel: onClose,
            onRequestClose: onClose,
            cancelText: t('action_close'),
            overlayClassName: classes.overlay,
        }),
        [classes.overlay, isOpen, onClose, t],
    );

    const descriptionModal = useMemo(() => {
        if (isReadOnly) {
            if (!isEllipsisActive) return null;
            return (
                <>
                    <Icon name="visibilityOn" size="medium" onClick={showDescriptionForm} />
                    <Modal useHeader={false} {...modalViewProps}>
                        <Text>{data?.[otherFields?.secondaryTextKey]}</Text>
                    </Modal>
                </>
            );
        } else {
            return (
                <>
                    <Icon name="edit" size="medium" onClick={showDescriptionForm} />
                    <Modal useHeader={false} {...modalEditProps}>
                        <Textarea
                            className={classes.textarea}
                            placeholder={t('placeholder_text_field')}
                            minRows={8}
                            onChange={setDescription}
                            value={description}
                            isFullWidth
                        />
                    </Modal>
                </>
            );
        }
    }, [
        classes.textarea,
        data,
        description,
        isEllipsisActive,
        isReadOnly,
        modalEditProps,
        modalViewProps,
        otherFields?.secondaryTextKey,
        showDescriptionForm,
        t,
    ]);

    if (withEmptyPlaceholder && !value) value = t('label_placeholder_unamed_record');
    const carouselCellTextClassName = classnames('w5-carousel-cell', {
        'fm-entity-placeholder': withEmptyPlaceholder && !value,
    });

    return (
        <div className="w5-cell" data-rowid={node.id}>
            <div className={carouselCellClassName}>
                <div className="w5-carousel-cell__img-container">{imageBox}</div>
                <div className="w5-carousel-cell__text-wrapper">
                    <div className={carouselCellTextClassName}>{value}</div>
                    <div className="w5-carousel-cell__description">
                        <span className="w5-carousel-cell__description-text" ref={descriptionEl}>
                            {data?.[otherFields?.secondaryTextKey]}
                        </span>
                        {descriptionModal}
                    </div>
                </div>
            </div>
        </div>
    );
};

CarouselCell.propTypes = {
    imageSize: PropTypes.oneOf(['small', 'medium', 'big']),
    isSquared: PropTypes.bool,
};

export default memo(CarouselCell);
